<app-view-dashboard-component>
  @if ({
    rows: filteredRows$ | async,
    kpis: overviewKpis$ | async,
    clients: clientOptions$ | async,
    sites: siteOptions$ | async,
    owners: ownerOptions$ | async,
    calendarSites: calendarSites$ | async,
    trackingCards: trackingCards$ | async,
    financeCards: financeCards$ | async,
  }; as vm) {
    <div class="projects-page">
      <app-page-header-actions [eyebrow]="'nav.projects'"
        [title]="'projectsPage.title'"
        [description]="'projectsPage.description'"></app-page-header-actions>

      @if (vm.kpis) {
        <section class="projects-kpis">
          <app-insight-cards [items]="vm.kpis"
            (cardClick)="onInsightCard($event)"></app-insight-cards>
        </section>
      }

      <app-section-tabs [tabs]="sectionTabs"
        (selectedChange)="onSectionTabChange($event)"></app-section-tabs>

      @if (!isManager() && activeTab() !== 'calendar') {
        <app-mobile-filter-collapse>
          <section class="projects-toolbar">
            <input class="projects-search"
              type="search"
              [placeholder]="'projectsPage.searchPlaceholder' | translate"
              [value]="searchQuery()"
              (input)="setSearchQuery($any($event.target).value)" />

            <app-select-popover class="projects-filter"
              [value]="statusFilter()"
              (change)="setStatusFilter($any($event.target).value)">
              <ion-select-option value="all">{{ 'projectsPage.filters.allStatuses' | translate }}</ion-select-option>
              <ion-select-option value="active">{{ 'projectsPage.status.active' | translate }}</ion-select-option>
              <ion-select-option value="upcoming">{{ 'projectsPage.status.upcoming' | translate }}</ion-select-option>
              <ion-select-option value="late">{{ 'projectsPage.status.late' | translate }}</ion-select-option>
              <ion-select-option value="completed">{{ 'projectsPage.status.completed' | translate }}</ion-select-option>
            </app-select-popover>

            <app-select-popover class="projects-filter"
              [value]="clientFilter()"
              (change)="setClientFilter($any($event.target).value)">
              <ion-select-option value="all">{{ 'projectsPage.filters.allClients' | translate }}</ion-select-option>
              @for (client of vm.clients ?? []; track client.guid) {
                <ion-select-option [value]="client.guid">{{ client.name }}</ion-select-option>
              }
            </app-select-popover>

            <app-select-popover class="projects-filter"
              [value]="siteFilter()"
              (change)="setSiteFilter($any($event.target).value)">
              <ion-select-option value="all">{{ 'projectsPage.filters.allSites' | translate }}</ion-select-option>
              @for (site of vm.sites ?? []; track site.guid) {
                <ion-select-option [value]="site.guid">{{ site.name }}</ion-select-option>
              }
            </app-select-popover>

            <app-select-popover class="projects-filter"
              [value]="ownerFilter()"
              (change)="setOwnerFilter($any($event.target).value)">
              <ion-select-option value="all">{{ 'projectsPage.filters.allOwners' | translate }}</ion-select-option>
              @for (owner of vm.owners ?? []; track owner) {
                <ion-select-option [value]="owner">{{ owner }}</ion-select-option>
              }
            </app-select-popover>
          </section>
        </app-mobile-filter-collapse>
      }

      @switch (activeTab()) {
        @case ('calendar') {
          <section class="projects-surface">
            <div class="projects-surface__header">
              <h2>{{ 'projectsPage.calendar.title' | translate }}</h2>
            </div>

            <div class="calendar-controlbar">

              <div class="calendar-period-label">
                {{ calendarPeriodLabel() }}
              </div>

              <div class="calendar-view-switch">
                <button type="button"
                  [class.active]="calendarView() === 'day'"
                  (click)="setCalendarView('day')">{{ 'projectsPage.calendar.day' | translate }}</button>
                <button type="button"
                  [class.active]="calendarView() === 'week'"
                  (click)="setCalendarView('week')">{{ 'projectsPage.calendar.week' | translate }}</button>
              </div>

              <div class="calendar-nav">
                <button type="button"
                  (click)="previousCalendarPeriod()">‹</button>
                <button type="button"
                  (click)="nextCalendarPeriod()">›</button>
              </div>
            </div>

            @if (!isManager()) {
              <div class="calendar-toolbar-filters">
                <app-select-popover class="projects-filter"
                  [value]="siteFilter()"
                  (change)="setSiteFilter($any($event.target).value)">
                  <ion-select-option value="all">{{ 'projectsPage.filters.allSites' | translate }}</ion-select-option>
                  @for (site of vm.sites ?? []; track site.guid) {
                    <ion-select-option [value]="site.guid">{{ site.name }}</ion-select-option>
                  }
                </app-select-popover>

                <app-select-popover class="projects-filter"
                  [value]="ownerFilter()"
                  (change)="setOwnerFilter($any($event.target).value)">
                  <ion-select-option value="all">{{ 'projectsPage.filters.allOwners' | translate }}</ion-select-option>
                  @for (owner of vm.owners ?? []; track owner) {
                    <ion-select-option [value]="owner">{{ owner }}</ion-select-option>
                  }
                </app-select-popover>
              </div>
            }

            @if (loading()) {
              <app-loading></app-loading>
            } @else {
              <div class="calendar-stack">
                @for (site of vm.calendarSites ?? []; track site.guid) {
                  <app-calendar [skipHeader]="false"
                    [selectedDay]="selectedDay()"
                    [viewSegmentValue]="calendarView()"
                    [site]="site"
                    [showSite]="true"></app-calendar>
                } @empty {
                  <app-no-results></app-no-results>
                }
              </div>
            }
          </section>
        }
        @case ('tracking') {
          <section class="tracking-grid">
            @for (card of vm.trackingCards ?? []; track card.titleKey) {
              <article class="tracking-card">
                <div class="tracking-card__header">
                  <h2>{{ card.titleKey | translate }}</h2>
                  <span>{{ card.items.length }}</span>
                </div>

                <div class="tracking-card__list">
                  @for (row of card.items; track row.project.guid) {
                    <button class="tracking-item"
                      type="button"
                      (click)="onProject(row.project)">
                      <strong>{{ row.project.name }}</strong>
                      <span>{{ row.siteName }} • {{ row.clientName }}</span>
                    </button>
                  } @empty {
                    <div class="tracking-empty">
                      <strong>✔ {{ card.emptyMessageKey | translate }}</strong>
                    </div>
                  }
                </div>
              </article>
            }
          </section>
        }
        @case ('finance') {
          <section class="tracking-grid">
            @for (card of vm.financeCards ?? []; track card.titleKey) {
              <article class="tracking-card">
                <div class="tracking-card__header">
                  <h2>{{ card.titleKey | translate }}</h2>
                  <span>{{ card.value }}</span>
                </div>

                <div class="tracking-card__list">
                  @for (row of card.items; track row.project.guid) {
                    <button class="tracking-item"
                      type="button"
                      (click)="onProject(row.project)">
                      <strong>{{ row.project.name }}</strong>
                      <span>{{ row.clientName }} • {{ row.siteName }}</span>
                    </button>
                  } @empty {
                    <div class="tracking-empty">
                      <strong>{{ card.emptyMessageKey | translate }}</strong>
                    </div>
                  }
                </div>
              </article>
            }
          </section>
        }
        @default {
          <section class="projects-surface projects-surface--overview">
            <div class="projects-surface__header">
              <h2>{{ 'projectsPage.overview.allProjects' | translate }}</h2>
              <span>{{ 'projectsPage.overview.projectsCount' | translate:{ count: (vm.rows ?? []).length } }}</span>
            </div>

            <div class="projects-table">
              <div class="projects-table__head">
                <span>{{ 'projectsPage.columns.project' | translate }}</span>
                <span>{{ 'projectsPage.columns.period' | translate }}</span>
                <span aria-hidden="true"></span>
              </div>

              <div class="projects-table__body">
                @for (row of vm.rows ?? []; track row.project.guid) {
                  <button class="project-row"
                    type="button"
                    (click)="onProject(row.project)">
                    <div class="project-row__main">
                      <div class="project-row__header">
                        <strong>{{ row.project.name }}</strong>
                        <span class="status-pill"
                          [class.active]="row.status === 'active'"
                          [class.upcoming]="row.status === 'upcoming'"
                          [class.late]="row.status === 'late'"
                          [class.completed]="row.status === 'completed'">
                          {{ ('projectsPage.status.' + row.status) | translate }}
                        </span>
                      </div>
                      <div class="project-row__meta">
                        <span>{{ row.siteName }}</span>
                        <span>{{ row.clientName }}</span>
                        <span>{{ row.responsible }}</span>
                      </div>
                    </div>
                    <div class="project-row__middle">
                      <span class="project-row__period">{{ row.startDate | date:'d.M.yyyy' }} -
                        {{ row.endDate | date:'d.M.yyyy' }}</span>
                      <span
                        class="project-row__finance">{{ ('projectsPage.financeState.' + row.financialState) | translate }}</span>
                      <span class="progress-inline">
                        <span class="progress-track">
                          <span class="progress-fill"
                            [style.width.%]="row.progressPercent"></span>
                        </span>
                        <span class="progress-label">{{ 'projectsPage.overview.progress' | translate:{
                          percent: row.progressPercent,
                          completed: row.completedTasks,
                          total: row.totalTasks
                        } }}</span>
                      </span>
                    </div>
                    <div class="project-row__aside">
                      <span class="project-row__chevron">›</span>
                    </div>
                  </button>
                } @empty {
                  <app-no-results></app-no-results>
                }
              </div>
            </div>
          </section>
        }
      }
    </div>
  }
</app-view-dashboard-component>
